import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { close3dViewer } from "csi/service/rightPaneService";
import { useStore } from "csi/state/hooks";
import { FlexColumn, FlexRow, FullScreenDialog, IconButton, OverflowText, PaneHeader, Select, Text, ToggleButton, ToggleButtonGroup } from "designSystem/index";
import { black } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import CubeIcon from "./icon/CubeIcon";
import FabricIcon from "./icon/FabricIcon";
import SphereIcon from "./icon/SphereIcon";
import ThreeDViewer from "./viewers/ThreeDViewer";

const PREFIX = "ThreeDViewer";

const classes = {
	columnStyle: `${PREFIX}columnStyle`,
	containerStyle: `${PREFIX}containerStyle`,
	toolbarPlaceholder: `${PREFIX}toolbarPlaceholder`,
	viewStyle: `${PREFIX}viewStyle`,
};


const StyledSelect = styled(Select)(() => ({
	width: 120,
	marginRight: 8,
}));

const renderItemContent = ({ id }: any) => (
	<FlexRow
		alignItems="center"
		flexGrow={1}
		justifyContent="flex-start"
	>
		{
			id == "MaterialPrimitiveCube" ? (
				<CubeIcon />
			) : id == "MaterialPrimitiveSphere" ? (
				<SphereIcon />
			) : (
				<FabricIcon />
			)
		}
		<OverflowText
			marginLeft={2}
			res={id}
			tooltipProps={{ placement: "right" }}
		/>
	</FlexRow>
);

export const ThreeDViewerDialog: FunctionComponent = observer(() => {
	const rootStore = useStore();
	const { threeDStore } = rootStore;

	if (!threeDStore) {
		return null;
	}

	const { threeDTitle, viewerId, modelFiles, fileType, modelFile, defaultModelFile, setModelViewerFile, locationTitle } = threeDStore;
	const { setModelViewerUpdateVariant, variants, defaultVariantName } = threeDStore.canvasStore;
	const { hasBackSide, activeMaterialFace, toggleMaterialSide, materialGeometries, showPrimitiveSelector, activeMaterialGeometry, setMaterialGeometry } = threeDStore.materialSettingsStore;

	const isMaterial = viewerId == "3DMaterialViewer";

	return (
		<FullScreenDialog onClose={() => close3dViewer(false)} open>
			<FlexColumn
				flexGrow={1}
				flexShrink={1}
				growNoOverflow
				minWidth={0}
			>
				<PaneHeader
					onClose={() => close3dViewer(false)}
				>
					<FlexRow
						alignItems="center"
						flexGrow={1}
						justifyContent="flex-start"
					>
						<OverflowText
							bold
							size={16}
							text={threeDTitle}
							variant="h4"
						/>
						<IconButton
							customSize={20}
							design="iconOnly"
							sx={{
								marginLeft: 1,
							}}
							title={locationTitle}
							tooltipProps={{ placement: "right" }}
						>
							<InfoOutlinedIcon style={{ fontSize: 20 }} />
						</IconButton>
					</FlexRow>
					<Box className={classes.toolbarPlaceholder}>
						{
							!!defaultVariantName && !isMaterial && variants && (
								<StyledSelect
									defaultValue={defaultVariantName}
									items={variants}
									onSelectItem={setModelViewerUpdateVariant}
								/>
							)
						}
						{
							modelFiles?.length > 1 && defaultModelFile && (
								<StyledSelect
									defaultValue={defaultModelFile}
									items={modelFiles}
									onSelectItem={setModelViewerFile}
									value={modelFile}
								/>
							)
						}
						{
							showPrimitiveSelector && fileType === "u3m" && (
								<StyledSelect
									items={materialGeometries}
									onSelectItem={setMaterialGeometry}
									renderItemFunc={(item) => renderItemContent(item)}
									value={activeMaterialGeometry}
								/>
							)
						}
						{
							isMaterial && hasBackSide && (
								<ToggleButtonGroup
									aria-label="front or back"
									exclusive
									onChange={toggleMaterialSide}
									sx={{
										"& .MuiToggleButton-root": {
											padding: "4px 8px",
										},
									}}
									value={activeMaterialFace}
								>
									<ToggleButton value="front">
										<Text
											color={black[100]}
											res="Front"
										/>
									</ToggleButton>
									<ToggleButton value="back">
										<Text
											color={black[100]}
											res="Back"
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							)
						}
					</Box>
				</PaneHeader>
				<ThreeDViewer />
			</FlexColumn>
		</FullScreenDialog>
	);
});

export default ThreeDViewerDialog;